/**
 * FoodSearchModal.jsx
 * Food search with real backend integration (OpenFoodFacts + local DB)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Plus, RefreshCw, ChevronRight, Scale, Sparkles, Camera, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import debounce from 'lodash.debounce';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const FoodSearchModal = ({ isOpen, onClose, onSelectFood, onCreateProduct, portalToken, mealType: initialMealType }) => {
  const { t } = useTranslation();
  
  const MEAL_TYPES = [
    { key: 'breakfast', label: t('nutrition.meals.breakfast'), icon: '🌅' },
    { key: 'snack_morning', label: t('nutrition.meals.snack_morning'), icon: '🍎' },
    { key: 'lunch', label: t('nutrition.meals.lunch'), icon: '☀️' },
    { key: 'snack_afternoon', label: t('nutrition.meals.snack_afternoon'), icon: '🍪' },
    { key: 'dinner', label: t('nutrition.meals.dinner'), icon: '🌙' },
    { key: 'snack_evening', label: t('nutrition.meals.snack_evening'), icon: '🌜' },
  ];
  const getTimeSuggestedMeal = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 10) return 'breakfast';
    if (hour >= 10 && hour < 12) return 'snack_morning';
    if (hour >= 12 && hour < 14) return 'lunch';
    if (hour >= 14 && hour < 17) return 'snack_afternoon';
    if (hour >= 17 && hour < 21) return 'dinner';
    return 'snack_evening';
  };
  
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [servingSize, setServingSize] = useState(100);
  const [selectedMealType, setSelectedMealType] = useState(initialMealType || getTimeSuggestedMeal());
  const [aiCreating, setAiCreating] = useState(false);
  const [aiReviewProduct, setAiReviewProduct] = useState(null);
  const [aiEditValues, setAiEditValues] = useState(null);
  const [recentFoods, setRecentFoods] = useState([]);
  const [favoriteFoods, setFavoriteFoods] = useState([]);

  // Load recent foods + favorites when modal opens (shown before user types)
  useEffect(() => {
    if (!isOpen || !portalToken) return;
    const loadSuggestions = async () => {
      try {
        const [recentRes, favRes] = await Promise.allSettled([
          axios.get(`${API}/portal/nutrition/recent`, {
            params: { limit: 8 },
            headers: { Authorization: `Bearer ${portalToken}` }
          }),
          axios.get(`${API}/portal/nutrition/favorites`, {
            headers: { Authorization: `Bearer ${portalToken}` }
          })
        ]);
        if (recentRes.status === 'fulfilled') {
          const data = recentRes.value.data;
          setRecentFoods(Array.isArray(data) ? data : (data?.foods || []));
        }
        if (favRes.status === 'fulfilled') {
          const data = favRes.value.data;
          setFavoriteFoods(Array.isArray(data) ? data : (data?.favorites || []));
        }
      } catch {
        /* silent fallback — Empty-State Hint bleibt als Minimum */
      }
    };
    loadSuggestions();
  }, [isOpen, portalToken]);

  const handleAiCreate = async () => {
    if (!query || query.length < 3) return;
    setAiCreating(true);
    try {
      const res = await axios.post(`${API}/portal/nutrition/ai-create-product`,
        { name: query },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      const product = res.data;
      // Show review dialog instead of immediately saving
      setAiReviewProduct(product);
      setAiEditValues({
        name: product.name || query,
        calories: product.nutrition_per_100g?.calories || 0,
        protein: product.nutrition_per_100g?.protein || 0,
        carbs: product.nutrition_per_100g?.carbs || 0,
        fat: product.nutrition_per_100g?.fat || 0,
        fiber: product.nutrition_per_100g?.fiber || 0,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('food_search.ai_error'));
    } finally {
      setAiCreating(false);
    }
  };

  const handleAiReviewConfirm = async () => {
    if (!aiReviewProduct || !aiEditValues) return;
    const updatedProduct = {
      ...aiReviewProduct,
      name: aiEditValues.name,
      nutrition_per_100g: {
        ...aiReviewProduct.nutrition_per_100g,
        calories: parseFloat(aiEditValues.calories) || 0,
        protein: parseFloat(aiEditValues.protein) || 0,
        carbs: parseFloat(aiEditValues.carbs) || 0,
        fat: parseFloat(aiEditValues.fat) || 0,
        fiber: parseFloat(aiEditValues.fiber) || 0,
      }
    };
    toast.success(t('food_search.ai_created', { name: updatedProduct.name }));
    onSelectFood({
      ...updatedProduct,
      meal_type: selectedMealType,
      serving_size: servingSize
    });
    setAiReviewProduct(null);
    setAiEditValues(null);
    onClose();
  };

  const getDefaultConsumedTimeForMeal = (mealTypeValue) => {
    const mapping = {
      breakfast: '08:00',
      snack_morning: '10:00',
      lunch: '12:30',
      snack_afternoon: '16:00',
      dinner: '19:00',
      snack_evening: '21:00'
    };
    return mapping[mealTypeValue] || '12:30';
  };

  // Debounced search function
  const searchFoods = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery || searchQuery.length < 2) return;
      
      if (!portalToken) {
        console.warn('FoodSearchModal: No portal token available');
        toast.error('Bitte erst einloggen');
        return;
      }
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/portal/nutrition/search`, {
          params: { query: searchQuery, page_size: 15 },
          headers: { Authorization: `Bearer ${portalToken}` }
        });
        setResults(response.data.products || []);
      } catch (error) {
        console.error('Search error:', error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    }, 200),
    [portalToken]
  );

  useEffect(() => {
    if (query.length >= 2) {
      searchFoods(query);
    } else {
      setResults([]);
    }
  }, [query, searchFoods]);

  // Calculate nutrition based on serving size
  const calculateNutrition = (nutrition, grams) => {
    const factor = grams / 100;
    return {
      calories: Math.round((nutrition?.calories || 0) * factor),
      protein: Math.round((nutrition?.protein || 0) * factor * 10) / 10,
      carbs: Math.round((nutrition?.carbs || 0) * factor * 10) / 10,
      fat: Math.round((nutrition?.fat || 0) * factor * 10) / 10,
    };
  };

  // Add food to meal
  const handleAddFood = async () => {
    if (!selectedFood) return;
    
    const nutrition = calculateNutrition(selectedFood.nutrition_per_100g, servingSize);
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const consumedAt = getDefaultConsumedTimeForMeal(selectedMealType);
    
    try {
      await axios.post(
        `${API}/portal/nutrition/entries`,
        {
          date: today,
          meal_type: selectedMealType,
          consumed_at: consumedAt,  // Time when meal was consumed
          food_name: selectedFood.name,
          food_id: selectedFood.id,
          brand: selectedFood.brand,
          serving_size: servingSize,
          calories: nutrition.calories,
          protein: nutrition.protein,
          carbs: nutrition.carbs,
          fat: nutrition.fat,
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success(`${selectedFood.name} zu ${MEAL_TYPES.find(m => m.key === selectedMealType)?.label || 'Mahlzeit'} hinzugefügt`);
      onSelectFood?.(selectedFood, servingSize, selectedMealType);
      setSelectedFood(null);
      setQuery('');
      onClose();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  if (!isOpen) return null;

  // AI Review Dialog
  if (aiReviewProduct && aiEditValues) {
    return (
      <div className="absolute inset-0 bg-black/95 z-50 flex flex-col" data-testid="ai-review-dialog">
        <div className="p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <button onClick={() => { setAiReviewProduct(null); setAiEditValues(null); }} className="p-2 bg-zinc-800 rounded-lg">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
            <div>
              <h2 className="text-white font-bold">{t('food_search.ai_review_title')}</h2>
              <p className="text-zinc-500 text-xs">{t('food_search.ai_review_desc')}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Product Name */}
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">{t('food_search.product_name')}</label>
            <input
              type="text"
              value={aiEditValues.name}
              onChange={(e) => setAiEditValues({ ...aiEditValues, name: e.target.value })}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white text-sm focus:outline-none focus:border-yellow-500"
              data-testid="ai-review-name"
            />
          </div>

          {/* Sparkles Badge */}
          <div className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-xl px-3 py-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-purple-300 text-xs">KI-generierte Nährwerte</span>
          </div>

          {/* Nutrition Values per 100g */}
          <div>
            <p className="text-zinc-400 text-xs mb-3 font-medium">{t('food_search.per_100g_label')}</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'calories', label: t('food_search.calories_label'), color: 'text-yellow-400', unit: 'kcal' },
                { key: 'protein', label: t('food_search.protein_label'), color: 'text-blue-400', unit: 'g' },
                { key: 'carbs', label: t('food_search.carbs_label'), color: 'text-orange-400', unit: 'g' },
                { key: 'fat', label: t('food_search.fat_label'), color: 'text-purple-400', unit: 'g' },
                { key: 'fiber', label: t('food_search.fiber_label'), color: 'text-green-400', unit: 'g' },
              ].map(field => (
                <div key={field.key} className="bg-zinc-800/70 rounded-xl p-3">
                  <label className={`text-xs mb-1 block ${field.color}`}>{field.label}</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="0.1"
                      value={aiEditValues[field.key]}
                      onChange={(e) => setAiEditValues({ ...aiEditValues, [field.key]: e.target.value })}
                      className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm text-center focus:outline-none focus:border-yellow-500"
                      data-testid={`ai-review-${field.key}`}
                    />
                    <span className="text-zinc-500 text-xs min-w-[28px]">{field.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Serving Size */}
          <div className="bg-zinc-800/50 rounded-xl p-3">
            <label className="text-zinc-400 text-xs mb-2 block">{t('food_search.serving_size')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={servingSize}
                onChange={(e) => setServingSize(Math.max(1, parseInt(e.target.value) || 100))}
                className="w-24 px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-center text-sm focus:outline-none focus:border-yellow-500"
              />
              <span className="text-zinc-400 text-sm">g</span>
              <span className="text-yellow-400 text-sm ml-auto font-medium">
                = {Math.round((parseFloat(aiEditValues.calories) || 0) * servingSize / 100)} kcal
              </span>
            </div>
          </div>

          {/* Meal Type Selection */}
          <div>
            <p className="text-zinc-400 text-xs mb-2">{t('food_search.add_to_meal')}</p>
            <div className="grid grid-cols-3 gap-1.5">
              {MEAL_TYPES.map(meal => (
                <button
                  key={meal.key}
                  onClick={() => setSelectedMealType(meal.key)}
                  className={`px-2 py-2 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors ${
                    selectedMealType === meal.key
                      ? 'bg-yellow-500 text-black font-medium'
                      : 'bg-zinc-800 text-zinc-300'
                  }`}
                >
                  <span>{meal.icon}</span>
                  <span className="truncate">{meal.label.replace('Snack ', '')}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t border-zinc-700 flex gap-3">
          <button
            onClick={() => { setAiReviewProduct(null); setAiEditValues(null); }}
            className="flex-1 py-3 bg-zinc-800 rounded-xl text-zinc-300 font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleAiReviewConfirm}
            className="flex-1 py-3 bg-yellow-500 rounded-xl text-black font-bold flex items-center justify-center gap-2"
            data-testid="ai-review-confirm"
          >
            <Check className="w-5 h-5" />
            {t('food_search.confirm_save')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={onClose} className="p-2 bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
          <h2 className="text-white font-bold flex-1">{t('common.search')}</h2>
        </div>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('food_search.search_placeholder')}
            className="w-full pl-10 pr-4 py-3 bg-zinc-800 rounded-xl text-white placeholder-zinc-500"
            autoFocus
          />
          {loading && (
            <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-400 animate-spin" />
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {/* Empty state (before user types) — Recent foods + Favorites + Hint */}
        {query.length < 2 && !loading && (
          <div data-testid="mobile-food-search-empty-state-suggestions">
            {favoriteFoods.length === 0 && recentFoods.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-300 text-sm font-medium mb-1">Lebensmittel suchen</p>
                <p className="text-zinc-500 text-xs px-6">
                  Tippe mindestens 2 Zeichen ein, um aus OpenFoodFacts + deinen eigenen Produkten zu suchen.
                </p>
              </div>
            ) : (
              <>
                {favoriteFoods.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Favoriten
                    </h3>
                    <div className="space-y-1.5">
                      {favoriteFoods.slice(0, 5).map((food) => (
                        <button
                          key={`fav-${food.id || food.food_id}`}
                          onClick={() => setSelectedFood(food)}
                          className="w-full flex items-center gap-3 p-3 bg-zinc-800/60 rounded-xl border border-zinc-700/60 hover:bg-zinc-800 transition-colors text-left"
                          data-testid="mobile-food-search-favorite-item"
                        >
                          <Sparkles className="w-4 h-4 text-yellow-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">
                              {food.food_name || food.name || 'Favorit'}
                            </p>
                            {food.brand && (
                              <p className="text-zinc-500 text-xs truncate">{food.brand}</p>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-500" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {recentFoods.length > 0 && (
                  <div>
                    <h3 className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Zuletzt gegessen
                    </h3>
                    <div className="space-y-1.5">
                      {recentFoods.slice(0, 8).map((food, idx) => (
                        <button
                          key={`rec-${food.id || food.food_id || idx}`}
                          onClick={() => setSelectedFood(food)}
                          className="w-full flex items-center gap-3 p-3 bg-zinc-800/60 rounded-xl border border-zinc-700/60 hover:bg-zinc-800 transition-colors text-left"
                          data-testid="mobile-food-search-recent-item"
                        >
                          <RefreshCw className="w-4 h-4 text-zinc-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">
                              {food.food_name || food.name || 'Eintrag'}
                            </p>
                            {food.brand && (
                              <p className="text-zinc-500 text-xs truncate">{food.brand}</p>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-500" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {results.length === 0 && query.length >= 2 && !loading && (
          <div className="text-center py-8" data-testid="mobile-food-search-empty-state">
            <p className="text-zinc-500 mb-3">{t('food_search.no_results')} "{query}"</p>
            <div className="flex gap-2 justify-center">
              {onCreateProduct && (
                <button
                  onClick={() => onCreateProduct(query)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-yellow-500 text-black rounded-xl font-semibold text-sm"
                  data-testid="mobile-food-search-create-product-button"
                >
                  <Camera className="w-4 h-4" />
                  Mit Foto
                </button>
              )}
              <button
                onClick={handleAiCreate}
                disabled={aiCreating}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
                data-testid="mobile-food-search-ai-create-button"
              >
                <Sparkles className={`w-4 h-4 ${aiCreating ? 'animate-spin' : ''}`} />
                {aiCreating ? '...' : t('food_search.ai_create')}
              </button>
            </div>
          </div>
        )}

        {/* "Not what you're looking for?" banner when results exist */}
        {results.length > 0 && query.length >= 2 && !loading && (
          <div className="flex items-center gap-2 px-3 py-2.5 mb-1 bg-purple-900/30 rounded-xl border border-purple-500/20" data-testid="mobile-food-search-create-banner">
            <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />
            <span className="text-xs text-zinc-300 flex-1">
              "{query}" nicht dabei?
            </span>
            <button
              onClick={handleAiCreate}
              disabled={aiCreating || query.length < 3}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium disabled:opacity-50"
              data-testid="mobile-food-search-inline-ai-create-button"
            >
              <Sparkles className={`w-3 h-3 ${aiCreating ? 'animate-spin' : ''}`} />
              {aiCreating ? '...' : 'Jetzt erstellen'}
            </button>
          </div>
        )}
        
        {results.map((food) => {
          const nutrition = food.nutrition_per_100g || {};
          const isSelected = selectedFood?.id === food.id;
          
          return (
            <button
              key={food.id}
              onClick={() => setSelectedFood(isSelected ? null : food)}
              className={`w-full bg-zinc-800/70 rounded-xl p-3 text-left transition-colors ${
                isSelected ? 'ring-2 ring-yellow-500 bg-zinc-800' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                {food.image_url ? (
                  <img 
                    src={food.image_url} 
                    alt={food.name}
                    className="w-12 h-12 rounded-lg object-cover bg-zinc-700"
                  />
                ) : (
                  <div className="w-12 h-12 bg-zinc-700 rounded-lg flex items-center justify-center">
                    <Scale className="w-6 h-6 text-zinc-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{food.name}</p>
                  <p className="text-zinc-500 text-xs">{food.brand || 'Basis'}</p>
                </div>
                <div className="text-right">
                  <p className="text-yellow-400 text-sm font-medium">{Math.round(nutrition.calories || 0)}</p>
                  <p className="text-zinc-500 text-xs">kcal/100g</p>
                </div>
              </div>
              
              {/* Expanded View when selected */}
              {isSelected && (
                <div className="mt-3 pt-3 border-t border-zinc-700">
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      { label: 'Protein', value: nutrition.protein, color: 'text-blue-400' },
                      { label: 'Kohlenhy.', value: nutrition.carbs, color: 'text-orange-400' },
                      { label: 'Fett', value: nutrition.fat, color: 'text-purple-400' },
                      { label: 'Ballaststoff', value: nutrition.fiber, color: 'text-green-400' },
                    ].map(m => (
                      <div key={m.label} className="text-center">
                        <p className={`text-sm font-medium ${m.color}`}>{m.value || 0}g</p>
                        <p className="text-zinc-500 text-[10px]">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Serving Size */}
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-400 text-sm">Menge:</span>
                    <input
                      type="number"
                      value={servingSize}
                      onChange={(e) => setServingSize(Math.max(1, parseInt(e.target.value) || 100))}
                      className="w-20 px-2 py-1 bg-zinc-700 rounded text-white text-center text-sm"
                    />
                    <span className="text-zinc-400 text-sm">g</span>
                    <span className="text-yellow-400 text-sm ml-auto">
                      {Math.round(calculateNutrition(nutrition, servingSize).calories || 0)} kcal
                    </span>
                  </div>
                  
                  {/* Meal Type Selection - only show if no meal preset */}
                  {!initialMealType && (
                  <div className="mt-3 pt-3 border-t border-zinc-700">
                    <p className="text-zinc-400 text-xs mb-2">Zu welcher Mahlzeit hinzufügen?</p>
                    <div className="grid grid-cols-3 gap-1">
                      {MEAL_TYPES.map(meal => (
                        <button
                          key={meal.key}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMealType(meal.key);
                          }}
                          className={`px-2 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors ${
                            selectedMealType === meal.key 
                              ? 'bg-yellow-500 text-black font-medium' 
                              : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                          }`}
                        >
                          <span>{meal.icon}</span>
                          <span className="truncate">{meal.label.replace('Snack ', '')}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Add Button */}
      {selectedFood && (
        <div className="p-4 border-t border-zinc-700">
          <button
            onClick={handleAddFood}
            className="w-full py-3 bg-yellow-500 rounded-xl text-black font-bold flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Zu {MEAL_TYPES.find(m => m.key === selectedMealType)?.label} hinzufügen
          </button>
        </div>
      )}
    </div>
  );
};

export default FoodSearchModal;
